import React, { useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import {
  Card,
  Picker,
  Grid,
  Row,
  Span,
  SimpleCollapse,
  TableCellStyled,
  TableContainerStyled,
  TableHeadStyled,
  TableRowStyled,
  TableStyled,
  TableBodyStyled,
  Col,
  CardChart,
  AutoCompleteInput
} from 'components'
//import { BalanceArrowDownIcon, BalanceArrowUpIcon } from 'components/Icons'
import { useDebounce, useModal, useLoading } from 'hooks'
import moment from 'moment'
import { useForm } from 'react-hook-form'
import {
  CockpitServices,
  IDashboardAnalyst,
  IInformacaoSemanal,
  IInformacaoSeparadaSemanalmente
} from 'services/NGT/Cockpit'
import { UserCsnService } from 'services/Supplier/Users'
import { SPACING, COLORS, FORMAT } from 'utils'
import { getMask } from 'utils/masks'

import { SCHEMA } from './yup/schema'

interface IAnalystCockpitForm {
  dataInicio: Date
  competenciaFim: Date
  competenciaInicio: Date
  dataFim: Date
}

const DEFAULT_FORM: IAnalystCockpitForm = {
  dataInicio: null,
  dataFim: null,
  competenciaInicio: null,
  competenciaFim: null
}

const AnalystCockpit: React.FC = () => {
  const { showModal } = useModal()

  const {
    setValue,
    trigger,
    watch,
    control,
    formState: { errors }
  } = useForm<IAnalystCockpitForm>({
    defaultValues: DEFAULT_FORM,
    resolver: yupResolver(SCHEMA)
  })
  const { setLoading } = useLoading()
  const [analistas, setAnalistas] = useState<
    {
      label: string
      value: any
    }[]
  >([{ label: '', value: {} }])
  const [selectedAnalitas, setSelectedAnalistas] = useState<any>('')
  const [idAnalyst, setIdAnalyst] = useState<number>()
  const [dashboardAnalystData, setDashboardAnalystData] =
    useState<IDashboardAnalyst>()
  const [infoSemanal, setInfoSemanal] =
    useState<IInformacaoSeparadaSemanalmente[]>()
  const [qtdEfetivo, setQtdEfetivo] = useState<number>(0)
  const [totalCre, setTotalCre] = useState(0)
  const [totalEffective, setTotalEffective] = useState(0)
  // const [competenciaInicio, setCompetenciaInicio] = useState<string>(null);
  // const [competenciaFim, setCompetenciaFim] = useState<string>(null);
  const [totalPendency, setTotalPendency] = useState(0)

  const dataInicio = watch('dataInicio')
  const dataFim = watch('dataFim')
  const competenciaInicio = watch('competenciaInicio');
  const competenciaFim = watch('competenciaFim')

  const handleSearchAnalista = (event) => {
    setAnalistas([])
    setLoading('loading')

    if (moment(dataInicio).isValid() && moment(dataFim).isValid()) {

      if (event.target.value == '') return

      CockpitServices()
        .getByNome(event.target.value, true, moment(dataInicio).toDate(), moment(dataFim).toDate())
        .then((response) => {
          setAnalistas(
            response.data.map((analista) => ({
              label: analista.nmUsuario,
              value: analista.idUsuario
            }))
          )
          setLoading('idle')
        })
        .catch(() => {
          setLoading('idle')
          showModal({
            hideButtonCancel: true,
            message: 'Não foi possível obter os dados do Analista',
            txtButtonConfirm: 'OK',
            type: 'error'
          })
        })
    } else {
      UserCsnService()
        .getByNome(event.target.value, true, true)
        .then((response) => {
          setAnalistas(
            response.data.map((analista) => ({
              label: analista.usuario.nmUsuario + ' - ' + analista.nmMatricula,
              value: analista.usuarioId
            }))
          )
          setLoading('idle')
        })
        .catch(() => {
          setLoading('idle')
          showModal({
            hideButtonCancel: true,
            message: 'Não foi possível obter os dados do Analista',
            txtButtonConfirm: 'OK',
            type: 'error'
          })
        })
    }
  }

  const handleSelectedValue = (selectedValue, value) => {

    setSelectedAnalistas(selectedValue)
    setIdAnalyst(value || null)
  }

  const getDashboardData = () => {
    if (
      dataInicio &&
      moment(dataInicio).isValid() &&
      dataFim &&
      moment(dataFim).isValid()
    ) {
      setLoading('loading')

      CockpitServices()
        .getDashboard(
          moment(dataInicio).toDate(),
          moment(dataFim).toDate(),
          idAnalyst,
          moment(competenciaInicio).format('YYYYMM'),
          moment(competenciaFim).format('YYYYMM')
        )
        .then(({ data }) => {
          setDashboardAnalystData(data)
          setInfoSemanal(data?.informacaoSemanal)
          setQtdEfetivo(data?.qtdEfetivo || 0)
          setLoading('idle')
        })
        .catch(() => {
          showModal({
            hideButtonCancel: true,
            message: 'Não foi possível obter dados da pesquisa',
            txtButtonConfirm: 'OK',
            type: 'error'
          })
          setLoading('idle')
        })
    }
  }

  const handleDataWeeksTables = () => {
    const totalCre =
      infoSemanal && infoSemanal.length > 0
        ? infoSemanal
          ?.map((x) => x?.informacaoSemanal)
          ?.map((y) =>
            y.map((x) => x?.qtdCreAnalisado)?.reduce((a, b) => a + b)
          )
          ?.reduce((a, b) => a + b)
        : 0

    const totalPendente =
      infoSemanal && infoSemanal.length > 0
        ? infoSemanal
          ?.map((x) => x?.informacaoSemanal)
          ?.map((y) =>
            y.map((x) => x?.qtdPendenteAnalisado)?.reduce((a, b) => a + b)
          )
          ?.reduce((a, b) => a + b)
        : 0

    setTotalCre(totalCre)
    setTotalPendency(totalPendente)
    setTotalEffective(dashboardAnalystData?.qtdEfetivo ?? 0)
  }

  useEffect(() => {
    handleDataWeeksTables()
  }, [dashboardAnalystData, infoSemanal])

  useEffect(() => {
    if (moment(dataInicio).isAfter(dataFim) || competenciaInicio && !competenciaFim || !moment(competenciaFim).isValid() || !moment(competenciaInicio).isValid()) return
    getDashboardData()
  }, [dataInicio, dataFim, idAnalyst, competenciaInicio, competenciaFim])

  useEffect(() => {
    // validação do yup ao digitar no campo
    const subs = watch(() => trigger())
    return () => subs.unsubscribe()
  }, [watch])

  return (
    <>
      <Card
        marginLeft={SPACING.spacing20}
        marginRight={SPACING.spacing20}
        marginBottom={SPACING.spacing100}
        marginTop={SPACING.spacing20}
        padding={SPACING.spacing20}
        boxShadow={`0px -1px 32px rgba(50, 50, 71, 0.12), 0px 24px 32px rgba(50, 50, 71, 0.12)`}
        borderRadius={`0px 10px 0px 0px`}
      >
        <Grid
          gridCols="0.7fr 0.7fr 0.6fr 0.6fr 3fr"
          gridGap={`0 ${SPACING.spacing20}`}
          margin={`${SPACING.spacing10} 0 ${SPACING.spacing30} 0`}
        >
          <Picker
            control={control}
            errors={errors}
            formValue="dataInicio"
            label="Data Inicial"
            value={dataInicio}
            placeholder="dd/mm/aaaa"
            type="dayMonthYear"
            inputFormat="dd/MM/yyyy"
          />
          <Picker
            control={control}
            errors={errors}
            formValue="dataFim"
            label="Data Final"
            value={dataFim}
            placeholder="dd/mm/aaaa"
            type="dayMonthYear"
            inputFormat="dd/MM/yyyy"
          />

          <Picker
            value={competenciaInicio}
            control={control}
            errors={errors}
            formValue="competenciaInicio"
            label="Competência Inicio"
            showClearButton={false}
            placeholder="mm/aaaa"
            views={['year', 'month']}
            inputFormat="MM/yyyy"
            type="monthAndYear"
            marginLeft={SPACING.spacing20}
          />
          <Picker
            value={competenciaFim}
            control={control}
            errors={errors}
            formValue="competenciaFim"
            label="Competência Fim"
            showClearButton={false}
            minDate={competenciaInicio}
            placeholder="mm/aaaa"
            views={['year', 'month']}
            inputFormat="MM/yyyy"
            type="monthAndYear"
            marginLeft={SPACING.spacing20}
          />

          <AutoCompleteInput
            name="nomeAnalista"
            marginTop={SPACING.spacing5}
            onChangeText={useDebounce(handleSearchAnalista, 600)}
            value={selectedAnalitas || null}
            options={analistas || []}
            optionValue="label"
            label="Nome Analista"
            noFilter
            onChange={(selectedValue, value) => {
              handleSelectedValue(selectedValue, value)
            }}
            valueName="value"
          />
        </Grid>
        <Row justifyContent="center">
          {selectedAnalitas && (
            <Span
              fontWeight="bold"
              marginTop={SPACING.spacing30}
              marginBottom={SPACING.spacing30}
              color={COLORS.blueFonts}
              text={`${selectedAnalitas}`}
              fontSize="1.2rem"
            />
          )}
        </Row>
        {dashboardAnalystData && (
          <>
            {infoSemanal && infoSemanal.length > 0 ? (
              <>
                {infoSemanal &&
                  infoSemanal.length > 0 &&
                  infoSemanal?.map((informacao, keyInfo) => (
                    <SimpleCollapse
                      title={`Semana ${informacao.numeroSemana}`}
                      key={keyInfo}
                    >
                      <TableContainerStyled>
                        <TableStyled>
                          <TableHeadStyled>
                            <TableRowStyled>
                              <TableCellStyled align="left" minwidth="20px">
                                Análise
                              </TableCellStyled>
                              {informacao &&
                                informacao?.informacaoSemanal?.length > 0 &&
                                informacao?.informacaoSemanal?.map(
                                  (value, key) => (
                                    <TableCellStyled
                                      tabIndex={key}
                                      align="center"
                                      minwidth="50px"
                                    >
                                      {FORMAT.formatDDMMYYYY(
                                        new Date(
                                          value.ano,
                                          value.mes - 1,
                                          value.dia
                                        )
                                      )}
                                    </TableCellStyled>
                                  )
                                )}
                            </TableRowStyled>
                          </TableHeadStyled>
                          <TableBodyStyled>
                            <TableRowStyled>
                              <TableCellStyled align="left" key="CRE">
                                CRE
                              </TableCellStyled>
                              {informacao &&
                                informacao?.informacaoSemanal?.length > 0 &&
                                informacao?.informacaoSemanal?.map(
                                  (row, key) => (
                                    <TableCellStyled align="center" key={key}>
                                      {row.qtdCreAnalisado}
                                    </TableCellStyled>
                                  )
                                )}
                            </TableRowStyled>
                            <TableRowStyled>
                              <TableCellStyled align="left" key="PENDENCIA">
                                PENDENCIA
                              </TableCellStyled>
                              {informacao &&
                                informacao?.informacaoSemanal?.length > 0 &&
                                informacao?.informacaoSemanal?.map(
                                  (row, key) => (
                                    <TableCellStyled align="center" key={key}>
                                      {row.qtdPendenteAnalisado}
                                    </TableCellStyled>
                                  )
                                )}
                            </TableRowStyled>
                          </TableBodyStyled>
                        </TableStyled>
                      </TableContainerStyled>
                    </SimpleCollapse>
                  ))}
                <Card
                  backgroundColor={COLORS.tableColor}
                  padding={SPACING.spacing10}
                  marginTop={SPACING.spacing20}
                >
                  <Row justifyContent="center">
                    <Span
                      fontWeight="bold"
                      color={COLORS.blueFonts}
                      text="ANÁLISE COMPETÊNCIA"
                      fontSize="1rem"
                    />
                  </Row>
                </Card>
                <Grid
                  gridCols="1fr 1fr 1fr"
                  gridGap={`0 ${SPACING.spacing20}`}
                  margin={`${SPACING.spacing30} 0 ${SPACING.spacing10} 0`}
                >
                  <Col justifyContent="center" alignItems="center">
                    <Span
                      fontWeight="bold"
                      color={COLORS.blueFonts}
                      text="CRE"
                      fontSize="1rem"
                      marginBottom={SPACING.spacing10}
                    />
                    <Row>
                      <Span
                        text={totalCre?.toLocaleString('pt-BR') || '0'}
                        fontWeight="bold"
                        fontSize="1rem"
                      />
                    </Row>
                  </Col>
                  <Col justifyContent="center" alignItems="center">
                    <Span
                      fontWeight="bold"
                      color={COLORS.blueFonts}
                      text="TOTAL EFETIVO"
                      fontSize="1rem"
                      marginBottom={SPACING.spacing10}
                    />
                    <Row>
                      <Span
                        fontWeight="bold"
                        text={qtdEfetivo?.toLocaleString('pt-BR') || '0'}
                        fontSize="1rem"
                      />
                    </Row>
                  </Col>
                  <Col justifyContent="center" alignItems="center">
                    <Span
                      fontWeight="bold"
                      color={COLORS.blueFonts}
                      text="PENDÊNCIA"
                      fontSize="1rem"
                      marginBottom={SPACING.spacing10}
                    />
                    <Row>
                      <Span
                        text={totalPendency?.toLocaleString('pt-BR') || '0'}
                        fontWeight="bold"
                        fontSize="1rem"
                      />
                    </Row>
                  </Col>
                </Grid>
              </>
            ) : (
              <TableContainerStyled>
                <TableStyled>
                  <TableHeadStyled>
                    <TableRowStyled>
                      <TableCellStyled align="center" minwidth="20px">
                        Não foram encontrados dados de CRE/Pendencias
                        analisados(a) no periodo informado.
                      </TableCellStyled>
                    </TableRowStyled>
                  </TableHeadStyled>
                </TableStyled>
              </TableContainerStyled>
            )}
            <div style={{ height: SPACING.spacing20 }}></div>

            <Grid
              gridCols="1fr 1fr 1fr 1fr 1fr 1fr 1fr"
              gridGap={`0 ${SPACING.spacing20}`}
              margin={`${SPACING.spacing20} 0 ${SPACING.spacing10} 0`}
            >
              <CardChart
                headerFontSize="1rem"
                titleCard={FORMAT.currency(
                  dashboardAnalystData?.informacaoValorRisco
                    .valorFRLiberadoCompetencia || 0
                )}
              >
                <Span
                  text="TOTAL FR LIBERADA"
                  fontSize="1rem"
                  marginTop={SPACING.spacing15}
                  marginBottom={SPACING.spacing10}
                />
                <Span
                  text="NA COMPETÊNCIA"
                  fontSize="1rem"
                  marginBottom={SPACING.spacing15}
                />
              </CardChart>

              <CardChart
                headerFontSize="1rem"
                titleCard={FORMAT.currency(
                  dashboardAnalystData?.informacaoValorRisco
                    .valorFRLiberadoGeral || 0
                )}
              >
                <Span
                  text="TOTAL FR LIBERADA"
                  fontSize="1rem"
                  marginTop={SPACING.spacing15}
                  marginBottom={SPACING.spacing10}
                />
                <Span
                  text="NO GERAL"
                  fontSize="1rem"
                  marginBottom={SPACING.spacing15}
                />
              </CardChart>

              <CardChart
                headerFontSize="1rem"
                titleCard={FORMAT.currency(
                  dashboardAnalystData?.informacaoValorRisco
                    .valorFRBloqueadoGeral || 0
                )}
              >
                <Span
                  text="TOTAL FR BLOQUEADA"
                  fontSize="1rem"
                  marginTop={SPACING.spacing15}
                  marginBottom={SPACING.spacing10}
                />
                <Span
                  text="NO GERAL"
                  fontSize="1rem"
                  marginBottom={SPACING.spacing15}
                />
              </CardChart>

              <CardChart
                headerFontSize="1rem"
                titleCard={FORMAT.currency(
                  dashboardAnalystData?.informacaoValorRisco
                    .valorFRBloqueadoCompetencia || 0
                )}
              >
                <Span
                  text="TOTAL FR BLOQUEADA"
                  fontSize="1rem"
                  marginTop={SPACING.spacing15}
                  marginBottom={SPACING.spacing10}
                />
                <Span
                  text="NA COMPETÊNCIA"
                  fontSize="1rem"
                  marginBottom={SPACING.spacing15}
                />
              </CardChart>

              <CardChart
                headerFontSize="1rem"
                titleCard={FORMAT.currency(
                  dashboardAnalystData?.informacaoValorRisco.valorRiscoGeral ||
                  0
                )}
              >
                <Span
                  text="VALOR DO RISCO"
                  fontSize="1rem"
                  marginTop={SPACING.spacing15}
                  marginBottom={SPACING.spacing10}
                />
                <Span
                  text="GERAL"
                  fontSize="1rem"
                  marginBottom={SPACING.spacing15}
                />
              </CardChart>

              <CardChart
                type="success"
                headerFontSize="1rem"
                titleCard={FORMAT.currency(
                  dashboardAnalystData?.informacaoValorRisco
                    .valorRiscoEvitado || 0
                )}
              >
                <Span
                  text="VALOR DO RISCO"
                  fontSize="1rem"
                  marginTop={SPACING.spacing15}
                  marginBottom={SPACING.spacing10}
                />
                <Span
                  text="EVITADO"
                  fontSize="1rem"
                  marginBottom={SPACING.spacing15}
                />
              </CardChart>

              <CardChart
                type="danger"
                headerFontSize="1rem"
                titleCard={FORMAT.currency(
                  dashboardAnalystData?.informacaoValorRisco
                    .valorRiscoNaoEvitado || 0
                )}
              >
                <Span
                  text="VALOR DO RISCO"
                  fontSize="1rem"
                  marginTop={SPACING.spacing15}
                  marginBottom={SPACING.spacing10}
                />
                <Span
                  text="NÃO EVITADO"
                  fontSize="1rem"
                  marginBottom={SPACING.spacing15}
                />
              </CardChart>
            </Grid>
          </>
        )}
      </Card>
    </>
  )
}

export default AnalystCockpit
